import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import BasePageLayout from '../../components/BasePageLayout';
import { useAuth } from '../../context/AuthContext';
import { useCountry } from '../../context/CountryContext';
import { useLanguage } from '../../context/LanguageContext';
import { TeamService } from '../../services/teamService';
import { supabase } from '../../services/supabaseClient';
import { TeamRequest, TeamApplication, Team } from '../../types/team';
import { useDeviceType, DeviceType } from '../../hooks/useDeviceType';
import { showToast } from '../../utils/toast';
import '../../styles/pages/CreateTaskPage.css';
import '../../styles/pages/CreateQuestionnairePage.css';
import '../../styles/pages/SetupTeamPage.css';
import '../../styles/layout/wide-screen-layouts.css';

import TeamRequestCard from '../../components/teams/TeamRequestCard';
import TeamDetailsPanel from '../../components/teams/TeamDetailsPanel';
import TeamManagementModal from '../../components/teams/TeamManagementModal';

type SetupTeamTab = 'open' | 'pending-review' | 'my-teams';

const SetupTeamPage: React.FC = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const { selectedCountry } = useCountry();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<SetupTeamTab>('open');
  const { deviceType } = useDeviceType();
  const [selectedTeamRequest, setSelectedTeamRequest] = useState<TeamRequest | null>(null);
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [showTeamDetail, setShowTeamDetail] = useState<boolean>(false);
  const [showTeamManagement, setShowTeamManagement] = useState<boolean>(false);
  const [stats, setStats] = useState({
    open: 0,
    pendingReview: 0,
    completed: 0,
    myTeams: 0
  });

  // Data states
  const [myRequests, setMyRequests] = useState<TeamRequest[]>([]);
  const [applicationsToMyRequests, setApplicationsToMyRequests] = useState<TeamApplication[]>([]);
  const [myTeams, setMyTeams] = useState<Team[]>([]);

  // Filter states
  const [searchTerm, setSearchTerm] = useState<string>('');

  // Application management states
  const [selectedApplication, setSelectedApplication] = useState<TeamApplication | null>(null);
  const [reviewMessage, setReviewMessage] = useState<string>('');

  // Team formation states
  const [isFormingTeam, setIsFormingTeam] = useState<boolean>(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Responsive state
  const [isCompactView, setIsCompactView] = useState(false);
  const [teamDetailsExpanded, setTeamDetailsExpanded] = useState<Record<string, boolean>>({});

  const loadData = useCallback(async () => {
    if (!user) return;

    try {
      const [myRequestsData, applicationsToMyRequestsData, myTeamsData] = await Promise.all([
        // My Requests: teams created by the user
        TeamService.getTeamRequests({ publisher_id: user.id }),
        // Applications to My Requests: applications to user's team requests for review
        TeamService.getApplicationsForMyRequests(user.id),
        // My Teams: teams where user is the owner/creator
        TeamService.getTeams({ owner_id: user.id })
      ]);

      setMyRequests(myRequestsData || []);
      setApplicationsToMyRequests(applicationsToMyRequestsData || []);
      setMyTeams(myTeamsData || []);

      // Calculate stats
      const openCount = myRequestsData?.filter(req => req.status === 'active').length || 0;
      const pendingReviewCount = applicationsToMyRequestsData?.filter(app => app.status === 'pending').length || 0;
      const completedCount = myRequestsData?.filter(req => req.status === 'completed').length || 0;
      const myTeamsCount = myTeamsData?.length || 0;

      setStats({
        open: openCount,
        pendingReview: pendingReviewCount,
        completed: completedCount,
        myTeams: myTeamsCount
      });

      console.log('Setup Team Data loaded:');
      console.log('- Open requests:', openCount);
      console.log('- Pending review applications:', pendingReviewCount);
      console.log('- Completed requests:', completedCount);
      console.log('- My teams:', myTeamsCount);

    } catch (error) {
      console.error('Error loading setup team data:', error);
    }
  }, [user, searchTerm]);

  useEffect(() => {
    loadData();
  }, [loadData]);



  // Handle URL parameter for pre-selecting a team
  useEffect(() => {
    const selectedTeamId = searchParams.get('selected');
    if (selectedTeamId && myRequests.length > 0) {
      const teamToSelect = myRequests.find(request => request.id === selectedTeamId);
      if (teamToSelect) {
        setSelectedTeamRequest(teamToSelect);
        setActiveTab('open');
      }
    }
  }, [searchParams, myRequests]);

  // Responsive effects
  useEffect(() => {
    const updateResponsiveState = () => {
      const width = window.innerWidth;
      const isCompact = width < 917;
      setIsCompactView(isCompact);
    };

    updateResponsiveState();

    const handleResize = () => {
      updateResponsiveState();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [deviceType]);



  // Toggle team details expansion in compact view
  const toggleTeamDetailsExpansion = useCallback((teamId: string) => {
    setTeamDetailsExpanded(prev => ({
      ...prev,
      [teamId]: !prev[teamId]
    }));
  }, []);

  const handleTeamRequestClick = (teamRequest: TeamRequest) => {
    setSelectedTeamRequest(teamRequest);
    setSelectedTeam(null);
    setShowTeamDetail(true);
  };

  // Handle team click for mobile
  const handleTeamClickMobile = useCallback((teamRequest: TeamRequest) => {
    if (isCompactView) {
      toggleTeamDetailsExpansion(teamRequest.id);
    } else {
      handleTeamRequestClick(teamRequest);
    }
  }, [isCompactView, toggleTeamDetailsExpansion]);

  const handleTeamClick = (team: Team) => {
    setSelectedTeam(team);
    setSelectedTeamRequest(null);
    setShowTeamDetail(true);
  };

  const handleCloseDetails = () => {
    setSelectedTeamRequest(null);
    setSelectedTeam(null);
    setSelectedApplication(null);
    setShowTeamDetail(false);
  };

  // Application Review Handlers
  const handleAcceptApplication = async (applicationId: string) => {
    try {
      await TeamService.acceptApplication(applicationId, reviewMessage);
      setReviewMessage('');
      await loadData();
      showToast(t('teams.applicationAcceptedToast'), 'success');
    } catch (error) {
      console.error('Error accepting application:', error);
      showToast(t('teams.failedToAcceptApplication'), 'error');
    }
  };

  const handleRejectApplication = async (applicationId: string) => {
    try {
      await TeamService.rejectApplication(applicationId, reviewMessage);
      setReviewMessage('');
      await loadData();
      showToast(t('teams.applicationRejectedToast'), 'success');
    } catch (error) {
      console.error('Error rejecting application:', error);
      showToast(t('teams.failedToRejectApplication'), 'error');
    }
  };

  // Team Formation Handler
  const handleFormTeam = async (teamRequestId: string) => {
    if (!user) return;

    setIsFormingTeam(true);
    try {
      // Get accepted applications for this team request
      const { data: acceptedApps, error } = await supabase
        .from('team_applications')
        .select(`
          id, 
          applicant:users!applicant_id(id, username, display_name)
        `)
        .eq('team_request_id', teamRequestId)
        .eq('status', 'accepted');

      if (error) throw error;

      if (!acceptedApps || acceptedApps.length === 0) {
        showToast(t('teams.noAcceptedApplications'), 'warning');
        return;
      }

      // Form the team
      const teamRequest = myRequests.find(req => req.id === teamRequestId);
      const teamName = teamRequest?.title || 'New Team';

      await TeamService.formTeam({
        request_id: teamRequestId,
        team_name: teamName,
        accepted_application_ids: acceptedApps.map(app => app.id)
      });

      await loadData();
      showToast(t('teams.teamFormedSuccess', { count: acceptedApps.length }), 'success');

    } catch (error) {
      console.error('Error forming team:', error);
      showToast(t('teams.failedToFormTeam'), 'error');
    } finally {
      setIsFormingTeam(false);
    }
  };

  // Filter data based on active tab
  const getCurrentTeamRequests = (): TeamRequest[] => {
    switch (activeTab) {
      case 'open':
        // Show team requests that are active and don't have pending applications waiting for review
        return myRequests.filter(req => {
          if (req.status !== 'active') return false;

          // Filter by search term if provided
          if (searchTerm.trim()) {
            const searchLower = searchTerm.toLowerCase();
            return req.title.toLowerCase().includes(searchLower) ||
              req.project_introduction.toLowerCase().includes(searchLower) ||
              (req.skills_required && req.skills_required.some(skill =>
                skill.toLowerCase().includes(searchLower)
              ));
          }

          return true;
        });
      default:
        return [];
    }
  };

  const getCurrentApplications = (): TeamApplication[] => {
    if (activeTab === 'pending-review') {
      let filteredApps = applicationsToMyRequests.filter(app => app.status === 'pending');

      // Filter by search term if provided
      if (searchTerm.trim()) {
        const searchLower = searchTerm.toLowerCase();
        filteredApps = filteredApps.filter(app =>
          (app.applicant?.display_name?.toLowerCase().includes(searchLower)) ||
          (app.applicant?.username?.toLowerCase().includes(searchLower)) ||
          (app.team_request?.title?.toLowerCase().includes(searchLower)) ||
          (app.introduction?.toLowerCase().includes(searchLower)) ||
          (app.skills_highlight && app.skills_highlight.some(skill =>
            skill.toLowerCase().includes(searchLower)
          ))
        );
      }

      return filteredApps;
    }
    return [];
  };

  const getCurrentTeams = (): Team[] => {
    if (activeTab === 'my-teams') {
      let filteredTeams = myTeams;

      // Filter by search term if provided
      if (searchTerm.trim()) {
        const searchLower = searchTerm.toLowerCase();
        filteredTeams = filteredTeams.filter(team =>
          team.name.toLowerCase().includes(searchLower) ||
          (team.description && team.description.toLowerCase().includes(searchLower))
        );
      }

      return filteredTeams;
    }
    return [];
  };



  // Team Type Buttons Configuration
  const teamTypeButtons = useMemo(() => [
    {
      id: 'open' as SetupTeamTab,
      label: t('teams.openRequests'),
      hint: t('teams.teamRequestsSeekingMembers'),
      icon: 'bi-unlock-fill',
      count: stats.open,
      gradient: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)'
    },
    {
      id: 'pending-review' as SetupTeamTab,
      label: t('teams.review'),
      hint: t('teams.applicationsWaitingForReview'),
      icon: 'bi-clock-history',
      count: stats.pendingReview,
      gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
    },
    {
      id: 'my-teams' as SetupTeamTab,
      label: t('teams.myTeams'),
      hint: t('teams.teamsYouOwnAndManage'),
      icon: 'bi-people-fill',
      count: stats.myTeams,
      gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
    }
  ], [t, stats.open, stats.pendingReview, stats.myTeams]);

  const handleTabSwitch = useCallback((tab: SetupTeamTab) => {
    if (teamTypeButtons.find(btn => btn.id === tab)?.count === 0) return;
    setActiveTab(tab);
    setSelectedTeamRequest(null);
    setSelectedApplication(null);
    setSelectedTeam(null);
  }, [teamTypeButtons]);

  return (
    <BasePageLayout background="white" padding="none" maxWidth="none">
      <div className="tasks-hub-container">
        {/* Compact Header */}
        <div className="compact-tasks-header">
          <div className="compact-header-content">
            {/* Title Section */}
            <div className="compact-title-section">
              <div>
                <h1 className="compact-page-title">{t('teams.teamSetupHub')}</h1>
                <div className="compact-breadcrumb">
                  <span>{t('navigation.teams')}</span>
                  <i className="bi bi-chevron-right compact-breadcrumb-separator"></i>
                  <span>{t('teams.setup')}</span>
                </div>
              </div>
              <div className="compact-stats-mini">
                <div className="stat-item-mini">
                  <span className="stat-number-mini">{stats.open + stats.pendingReview + stats.myTeams}</span>
                  <span className="stat-label-mini">{t('teams.totalItems')}</span>
                </div>
                <div className="stat-item-mini earnings">
                  <span className="stat-number-mini">{stats.completed}</span>
                  <span className="stat-label-mini">{t('teams.completed')}</span>
                </div>
              </div>
            </div>

            {/* Controls Section */}
            <div className="compact-controls-section">
              {/* Team Type Buttons */}
              <div className="compact-type-buttons">
                {teamTypeButtons.map(button => (
                  <button
                    key={button.id}
                    className={`compact-type-button compact-tooltip ${activeTab === button.id ? 'active' : ''} ${button.count === 0 ? 'disabled' : ''}`}
                    onClick={() => button.count > 0 && handleTabSwitch(button.id)}
                    disabled={button.count === 0}
                    data-tooltip={button.hint}
                    style={{
                      background: activeTab === button.id ? button.gradient : undefined
                    }}
                  >
                    <i className={`bi ${button.icon} compact-type-button-icon`}></i>
                    <span className="compact-type-button-label">{button.label}</span>
                    <span className="compact-type-button-count">{button.count}</span>
                  </button>
                ))}
              </div>

              {/* Search and Actions */}
              <div className="compact-search-filters">
                <div className="compact-search-bar">
                  <i className="bi bi-search compact-search-icon"></i>
                  <input
                    type="text"
                    className="compact-search-input"
                    placeholder={t('teams.searchTeamsPlaceholder')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <button
                  className="btn btn-primary"
                  onClick={() => navigate('/create-team-request')}
                  title={t('teams.createTeamRequest')}
                >
                  <i className="bi bi-plus-circle"></i>
                  {t('teams.createTeamButton')}
                </button>

              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="tasks-hub-main-two-column">

          {/* Team List */}
          <div className="tasks-hub-task-list-column">
            {error && (
              <div className="alert alert-danger">
                <i className="bi bi-exclamation-triangle"></i>
                {error}
              </div>
            )}

            <div className="tasks-hub-task-list-header">
              <span className="tasks-hub-results-count">
                {activeTab === 'open' && `${getCurrentTeamRequests().length} ${getCurrentTeamRequests().length === 1 ? t('teams.request') : t('teams.requests')}`}
                {activeTab === 'pending-review' && `${getCurrentApplications().length} ${getCurrentApplications().length === 1 ? t('teams.application') : t('teams.applications')}`}
                {activeTab === 'my-teams' && `${getCurrentTeams().length} ${getCurrentTeams().length === 1 ? t('teams.team') : t('teams.teams')}`}
              </span>
              <div className="tasks-hub-sort-controls">
                <button
                  onClick={loadData}
                  className="tasks-hub-refresh-button"
                  title={t('teams.refresh')}
                  disabled={loading}
                >
                  <i className={`bi bi-arrow-clockwise ${loading ? 'spinning' : ''}`}></i>
                </button>
              </div>
            </div>

            <div className="tasks-hub-task-list-content">
              {/* Open Requests */}
              {activeTab === 'open' && (
                <div className="tasks-hub-task-cards">
                  {getCurrentTeamRequests().length === 0 ? (
                    <div className="no-tasks-message">
                      <i className="bi bi-inbox"></i>
                      <h3>{t('teams.noOpenTeamRequests')}</h3>
                      <p>{t('teams.createTeamRequestToStart')}</p>
                      <button
                        className="btn btn-primary"
                        onClick={() => navigate('/create-team-request')}
                      >
                        <i className="bi bi-plus-circle"></i>
                        {t('teams.createTeamRequest')}
                      </button>
                    </div>
                  ) : (
                    getCurrentTeamRequests().map(teamRequest => {
                      // Get accepted applications count for this request
                      const acceptedAppsCount = applicationsToMyRequests.filter(
                        app => app.team_request_id === teamRequest.id && app.status === 'accepted'
                      ).length;

                      return (
                        <div
                          key={teamRequest.id}
                          className={`task-card-wrapper ${selectedTeamRequest?.id === teamRequest.id ? 'selected' : ''}`}
                          onClick={() => handleTeamRequestClick(teamRequest)}
                        >
                          <div className="task-card task-card-modern">
                            <div className="task-header-row">
                              <h3 className="task-title">{teamRequest.title}</h3>
                              <div className="task-status-badges">
                                <span className="task-points">
                                  <i className="bi bi-people-fill"></i>
                                  {acceptedAppsCount + 1}/{teamRequest.team_size}
                                </span>
                              </div>
                            </div>

                            <p className="task-description">{teamRequest.project_introduction}</p>

                            <div className="task-meta">
                              <div className="task-meta-left">
                                <span className="task-category">
                                  <i className="bi bi-clock"></i>
                                  {teamRequest.min_time_commitment}
                                </span>
                                {teamRequest.skills_required && teamRequest.skills_required.length > 0 && (
                                  <span className="task-verification-info">
                                    <i className="bi bi-tools"></i>
                                    {teamRequest.skills_required.slice(0, 2).join(', ')}
                                    {teamRequest.skills_required.length > 2 && ` +${teamRequest.skills_required.length - 2}`}
                                  </span>
                                )}
                              </div>
                              <div className="task-meta-right">
                                <span className="task-date">
                                  {new Date(teamRequest.created_at).toLocaleDateString()}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              )}

              {/* Pending Review Applications */}
              {activeTab === 'pending-review' && (
                <div className="tasks-hub-task-cards">
                  {getCurrentApplications().length === 0 ? (
                    <div className="no-tasks-message">
                      <i className="bi bi-inbox"></i>
                      <h3>{t('teams.noPendingApplications')}</h3>
                      <p>{t('teams.applicationsWillAppearHere')}</p>
                    </div>
                  ) : (
                    getCurrentApplications().map((application: TeamApplication) => (
                      <div
                        key={application.id}
                        className={`task-card-wrapper ${selectedApplication?.id === application.id ? 'selected' : ''}`}
                        onClick={() => {
                          setSelectedApplication(application);
                          setSelectedTeamRequest(null);
                          setSelectedTeam(null);
                          setShowTeamDetail(true);
                        }}
                      >
                        <div className="task-card task-card-modern">
                          <div className="task-header-row">
                            <h3 className="task-title">
                              {application.applicant?.display_name || application.applicant?.username}
                            </h3>
                            <div className="task-status-badges">
                              <span className="task-status-badge pending">
                                <i className="bi bi-clock-history"></i>
                                {t('teams.pendingLabel')}
                              </span>
                            </div>
                          </div>

                          <p className="task-description">
                            <strong>{t('teams.for')}</strong> {application.team_request?.title}
                          </p>
                          <p className="task-description">{application.introduction}</p>

                          <div className="task-meta">
                            <div className="task-meta-left">
                              {application.skills_highlight && application.skills_highlight.length > 0 && (
                                <span className="task-verification-info">
                                  <i className="bi bi-tools"></i>
                                  {application.skills_highlight.slice(0, 2).join(', ')}
                                  {application.skills_highlight.length > 2 && ` +${application.skills_highlight.length - 2}`}
                                </span>
                              )}
                            </div>
                            <div className="task-meta-right">
                              <span className="task-date">
                                {application.applied_at ? new Date(application.applied_at).toLocaleDateString() : t('teams.dateNotAvailable')}
                              </span>
                              <div className="application-quick-actions">
                                <button
                                  className="btn btn-success btn-sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleAcceptApplication(application.id);
                                  }}
                                  title={t('teams.accept')}
                                >
                                  <i className="bi bi-check-circle"></i>
                                </button>
                                <button
                                  className="btn btn-danger btn-sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRejectApplication(application.id);
                                  }}
                                  title={t('teams.reject')}
                                >
                                  <i className="bi bi-x-circle"></i>
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* My Teams */}
              {activeTab === 'my-teams' && (
                <div className="tasks-hub-task-cards">
                  {getCurrentTeams().length === 0 ? (
                    <div className="no-tasks-message">
                      <i className="bi bi-inbox"></i>
                      <h3>{t('teams.noTeamsCreatedYet')}</h3>
                      <p>{t('teams.teamsYouManageWillAppearHere')}</p>
                    </div>
                  ) : (
                    getCurrentTeams().map(team => (
                      <div
                        key={team.id}
                        className={`task-card-wrapper ${selectedTeam?.id === team.id ? 'selected' : ''}`}
                        onClick={() => handleTeamClick(team)}
                      >
                        <div className="task-card task-card-modern">
                          <div className="task-header-row">
                            <h3 className="task-title">{team.name}</h3>
                            <div className="task-status-badges">
                              <span className={`task-status-badge ${team.status === 'active' ? 'approved' : 'pending'}`}>
                                <i className="bi bi-circle-fill"></i>
                                {team.status.toUpperCase()}
                              </span>
                            </div>
                          </div>

                          {team.description && (
                            <p className="task-description">{team.description}</p>
                          )}

                          <div className="task-meta">
                            <div className="task-meta-left">
                              <span className="task-category">
                                <i className="bi bi-people-fill"></i>
                                {team.members_count || 0} {t('teams.members')}
                              </span>
                            </div>
                            <div className="task-meta-right">
                              <span className="task-date">
                                {new Date(team.created_at).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Overlay for when detail panel is open */}
          {showTeamDetail && (
            <div className="team-detail-overlay" onClick={handleCloseDetails}></div>
          )}

          {/* Team Detail Panel - Slide out from right */}
          <div className={`team-detail-panel ${showTeamDetail ? 'panel-open' : ''}`}>
            {selectedTeamRequest ? (
              <TeamDetailsPanel
                teamRequest={selectedTeamRequest}
                onClose={handleCloseDetails}
                type="request"
                onFormTeam={handleFormTeam}
                acceptedAppsCount={applicationsToMyRequests.filter(
                  app => app.team_request_id === selectedTeamRequest.id && app.status === 'accepted'
                ).length}
                isFormingTeam={isFormingTeam}
              />
            ) : selectedApplication ? (
              /* Application Details View for Review */
              <div className="task-details-container">
                <div className="task-details-header">
                  <div className="task-details-header-main">
                    <div className="task-details-title-area">
                      <h1 className="task-details-title">{t('teams.reviewApplication')}</h1>
                      <div className="task-details-metadata">
                        <span className="task-details-category-badge">
                          <i className="bi bi-person-fill"></i>
                          {selectedApplication.applicant?.display_name || selectedApplication.applicant?.username}
                        </span>
                        <span className={`application-status-badge status-${selectedApplication.status}`}>
                          {selectedApplication.status.toUpperCase()}
                        </span>
                        <span className="task-details-date-badge">
                          <i className="bi bi-calendar3"></i>
                          {t('teams.applied')} {selectedApplication.applied_at ? new Date(selectedApplication.applied_at).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric'
                          }) : t('teams.dateNotAvailable')}
                        </span>
                      </div>
                    </div>

                    <div className="task-details-header-actions">
                      <button
                        className="task-details-action-btn close"
                        onClick={() => {
                          setSelectedApplication(null);
                          setShowTeamDetail(false);
                        }}
                        title={t('teams.closeButton')}
                      >
                        <i className="bi bi-x-lg"></i>
                      </button>
                    </div>
                  </div>
                </div>

                <div className="task-details-content">
                  <div className="task-details-section">
                    <h3>{t('teams.teamRequestHeading')}</h3>
                    <p><strong>{selectedApplication.team_request?.title}</strong></p>
                    <p>{selectedApplication.team_request?.project_introduction}</p>
                  </div>

                  <div className="task-details-section">
                    <h3>{t('teams.introduction')}</h3>
                    <p>{selectedApplication.introduction}</p>
                  </div>

                  <div className="task-details-section">
                    <h3>{t('teams.whyGoodFitHeading')}</h3>
                    <p>{selectedApplication.why_fit}</p>
                  </div>

                  {selectedApplication.skills_highlight && selectedApplication.skills_highlight.length > 0 && (
                    <div className="task-details-section">
                      <h3>{t('teams.skillsHeading')}</h3>
                      <div className="skills-list">
                        {selectedApplication.skills_highlight.map((skill, index) => (
                          <span key={index} className="skill-tag">{skill}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="task-details-section">
                    <h3>{t('teams.reviewMessage')}</h3>
                    <textarea
                      className="form-control"
                      rows={3}
                      value={reviewMessage}
                      onChange={(e) => setReviewMessage(e.target.value)}
                      placeholder={t('teams.reviewMessagePlaceholder')}
                    />
                  </div>

                  <div className="task-details-actions">
                    <button
                      className="btn btn-success"
                      onClick={() => handleAcceptApplication(selectedApplication.id)}
                    >
                      <i className="bi bi-check-circle"></i>
                      {t('teams.acceptApplication')}
                    </button>
                    <button
                      className="btn btn-danger"
                      onClick={() => handleRejectApplication(selectedApplication.id)}
                    >
                      <i className="bi bi-x-circle"></i>
                      {t('teams.rejectApplicationButton')}
                    </button>
                  </div>
                </div>
              </div>
            ) : selectedTeam ? (
              <TeamDetailsPanel
                team={selectedTeam}
                onClose={handleCloseDetails}
                type="team"
                onManageTeam={(team) => {
                  setSelectedTeam(team);
                  setShowTeamManagement(true);
                }}
              />
            ) : (
              <div className="task-details-placeholder">
                <div className="placeholder-icon">
                  <i className="bi bi-arrow-left"></i>
                </div>
                <h3>{t('teams.selectAnItem')}</h3>
                <p>{t('teams.selectItemDescription')}</p>
              </div>
            )}
          </div>

        </div>
      </div>

      {/* Team Management Modal */}
      {showTeamManagement && selectedTeam && (
        <TeamManagementModal
          team={selectedTeam}
          onClose={() => {
            setShowTeamManagement(false);
            setSelectedTeam(null);
          }}
          onTeamUpdate={loadData}
        />
      )}
    </BasePageLayout>
  );
};

export default SetupTeamPage;
